import React, { useEffect, useState } from "react";

import { useAppStore } from "../../../../../context/store";
import { Dialog, Portal, Select, CloseButton, Button, createListCollection, Field, Box, Checkbox, CheckboxGroup } from "@chakra-ui/react";

import { floatingStyles, removeEpochSuffix, removeFileExtension, showToast } from "../../../../../utilies/Utility";
import { LuLayers } from "react-icons/lu";
import { getEditedGeoJSON } from "../../../../../utilies/geojson/GetEditedGeojson";
import Type from "../../../../../utilies/Types";
import { loadGeojsonTypeAsync } from "../../../../../utilies/loader/LoadGeojson";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
const LayersMerge = () => {
  // state
  const isShowMergeDialog = useAppStore((s) => s.isShowMergeDialog);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const defaultOsmStyle = useAppStore((s) => s.defaultOsmStyle);

  // setters / refs
  const setIsShowMergeDialog = useAppStore.getState().setIsShowMergeDialog;
  const viewerRef = useAppStore.getState().viewerRef;
  const originalCameraControls = useAppStore.getState().originalCameraControls;

  const [layersOptions, setLayersOptions] = useState(
    createListCollection({
      items: [],
    })
  );
  const [selectedLayerToPreview, setSelectedLayerToPreview] = useState(null);
  const [listOfLayerToSelect, setListOfLayerToSelect] = useState([]);
  const [selectedLayerIdsToMerge, setSelectedLayerIdsToMerge] = useState([]);

  useEffect(() => {
    if (!isShowMergeDialog) return;

    // helper: produce comparable normalized key
    const normalize = (name) => removeEpochSuffix(removeFileExtension(String(name).trim()).toLowerCase());

    const values = Object.values(mapObjects || {});

    // build normalized list
    const normalizedNames = values.map((v) => normalize(v.file_name));

    // count occurrences
    const counts = normalizedNames.reduce((acc, n) => {
      if (!n) return acc;
      acc[n] = (acc[n] || 0) + 1;
      return acc;
    }, {});

    // keep names that appear more than once
    const uniqueDuplicates = Object.keys(counts).filter((k) => counts[k] > 1);

    // map to label/value; take first matched original file_name cleaned
    const temp = uniqueDuplicates.map((name) => {
      const matched = values.find((obj) => normalize(obj.file_name) === name);
      const cleaned = removeEpochSuffix(removeFileExtension(matched?.file_name || ""));
      return { label: name, value: cleaned };
    });

    setLayersOptions(
      createListCollection({
        items: temp,
        itemToString: (item) => item.label,
        itemToValue: (item) => item.value,
      })
    );
  }, [isShowMergeDialog]);

  useEffect(() => {
    if (selectedLayerToPreview) {
      let listOfIds = [];
      for (const key in mapObjects) {
        if (removeEpochSuffix(removeFileExtension(mapObjects[key].file_name).toLowerCase()) == selectedLayerToPreview.toLowerCase()) {
          listOfIds.push({ label: mapObjects[key].file_name, value: mapObjects[key].id });
        }
      }
      setListOfLayerToSelect(listOfIds);
    }
  }, [selectedLayerToPreview]);

  const mergeLayers = () => {
    let s = [];

    for (const key in mapObjects) {
      if (selectedLayerIdsToMerge.includes(mapObjects[key].id)) {
        s.push(getEditedGeoJSON(mapObjects[key]));
      }
    }

    if (s.length <= 1) return;

    // Merge all GeoJSON FeatureCollections
    const merged = s.reduce((acc, curr) => {
      // Combine features of each collection into accumulator
      acc.features = [...acc.features, ...curr.features];
      return acc;
    });

    loadGeojsonTypeAsync({
      originalCameraControls,
      geojsonData: merged,
      fileName: selectedLayerToPreview + "_merged",
      viewerRef,
      setMapObjects,
      defaultGeojsonStyle,
      basicGeojsonStyle,
      defaultOsmStyle,
      isEdited: true,
    });

    setIsShowMergeDialog(false);
    showToast("Merged", Type.NOTIFICATION.SUCCESS);
  };

  return (
    <Dialog.Root
      lazyMount
      size={"sm"}
      open={isShowMergeDialog}
      closeOnInteractOutside={false}
      onOpenChange={(e) => {
        setIsShowMergeDialog(e.open);
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title> </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Field.Root mt={2}>
                <Box pos="relative" w="full">
                  <Select.Root
                    collection={layersOptions}
                    size="sm"
                    width="full"
                    onValueChange={(v) => {
                      setSelectedLayerToPreview(v.value[0]);
                    }}
                  >
                    <Select.HiddenSelect />

                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={"Layers"} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {layersOptions.items.map((item) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.Label css={floatingStyles}>
                    <LuLayers /> Layers
                  </Field.Label>
                </Box>
              </Field.Root>
              <CheckboxGroup
                mt={3}
                onValueChange={(v) => {
                  setSelectedLayerIdsToMerge(v);
                }}
              >
                {listOfLayerToSelect.map((item) => (
                  <Checkbox.Root key={item.value} value={item.value}>
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>{item.label}</Checkbox.Label>
                  </Checkbox.Root>
                ))}
              </CheckboxGroup>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button
                onClick={() => {
                  mergeLayers();
                }}
              >
                Merge
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default LayersMerge;
